import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  WeightTrackingAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { TextTestID, WeightTrackingTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import { useAppSelector } from "@/store/hooks";
import {
  selectUserPreferences,
  useUserPreferencesDispatch,
} from "@/store/reducers/userPreferences";
import EmptyWeightDataCard from "@/ui/components/vitals/EmptyWeightDataCard";
import BriefWeightEntries from "@/ui/components/vitals/overview/BriefWeightEntries";
import RecentWeightSummary from "@/ui/components/vitals/overview/RecentWeightSummary";
import WeightTrackingGraph from "@/ui/components/vitals/WeightTrackingGraph";
import { Button, FAIconName } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import CollapsibleContainer from "@/ui/layout/animated/CollapsibleContainer";
import { joinTestIDs } from "@/utils/string.util";
import {
  getLastWeekReferenceObservation,
  getLoggedBy,
  getPatientTrendValue,
} from "@/utils/vitals/weight.util";

const baseTestID = WeightTrackingTestID.Overview;

interface WeightTrackingOverviewProps {
  data: PatientObservationData[];
  selectedUserId: string;
}

const WeightTrackingCard = ({
  data,
  selectedUserId,
}: WeightTrackingOverviewProps) => {
  const { track } = useAnalytics();

  const [mostRecent] = data;
  const mostRecentValue = mostRecent.observation.value;
  const mostRecentUnit = mostRecent.observation.unit;
  const mostRecentLoggedBy = getLoggedBy(mostRecent);
  const nextMostRecent = data[1] || undefined;
  const lastObservationInWeek = getLastWeekReferenceObservation(data);
  const isWeeklyWeightChange = !isNil(lastObservationInWeek);
  const weightChange = getPatientTrendValue({
    recent: mostRecent,
    reference: lastObservationInWeek || nextMostRecent,
  });

  const handleViewHistoryPress = () => {
    track(WeightTrackingAnalyticsEvent.ViewWeightHistoryPressed, {
      [AnalyticsFieldName.Component]: WeightTrackingOverview.name,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: WeightTrackingOverview.name,
    });
    router.navigate({
      params: {
        parentSelectedUserId: selectedUserId,
      },
      pathname: ModalPath.WeightHistory,
    });
  };
  return (
    <BaseCard testID={joinTestIDs(baseTestID, TextTestID.Card)}>
      <VStack gap={18}>
        <RecentWeightSummary
          isWeeklyWeightChange={isWeeklyWeightChange}
          loggedBy={mostRecentLoggedBy}
          unit={mostRecentUnit}
          value={mostRecentValue}
          weightChange={weightChange}
        />
        <WeightTrackingGraph data={data.slice(0, 5)} />
        <BriefWeightEntries data={data.slice(1, 4)} />
        <Button
          onPress={handleViewHistoryPress}
          rightIcon={FAIconName.ChevronRight}
          testID={WeightTrackingTestID.History}
          title="View weight history"
          variant="tertiary"
        />
      </VStack>
    </BaseCard>
  );
};

const WeightTrackingOverview = ({
  data,
  selectedUserId,
}: WeightTrackingOverviewProps) => {
  const { track } = useAnalytics();
  const { isWeightPrivacyModeEnabled: isHidden } = useAppSelector(
    selectUserPreferences,
  );
  const isExpanded = !isHidden;
  const { setIsWeightPrivacyModeEnabled: setIsHidden } =
    useUserPreferencesDispatch();

  const handleToggleWeightPrivacyMode = () => {
    const { HideWeightPressed, ShowWeightPressed } =
      WeightTrackingAnalyticsEvent;
    const event = isExpanded ? HideWeightPressed : ShowWeightPressed;
    track(event, {
      [AnalyticsFieldName.Component]: WeightTrackingOverview.name,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: WeightTrackingOverview.name,
    });
    setIsHidden(!isHidden);
  };

  return (
    <VStack>
      <CollapsibleContainer isExpanded={isExpanded} minHeight={16.5}>
        <VStack flexDirection="row" paddingBottom={18}>
          {isEmpty(data) ? (
            <EmptyWeightDataCard testID={WeightTrackingTestID.Section} />
          ) : (
            <WeightTrackingCard data={data} selectedUserId={selectedUserId} />
          )}
        </VStack>
      </CollapsibleContainer>
      <HStack justifyContent="center">
        <Button
          onPress={handleToggleWeightPrivacyMode}
          rightIcon={isExpanded ? FAIconName.ChevronUp : FAIconName.ChevronDown}
          testID={baseTestID}
          title={`${isExpanded ? "Hide" : "Show"} weight`}
          variant="tertiary"
        />
      </HStack>
    </VStack>
  );
};

export type { WeightTrackingOverviewProps };
export default WeightTrackingOverview;
